// Biblioteca de motores del usuario: archivos .eng/.rse y un motor de demostración con curva sintética propia.
import { createMotor, parseEng, parseRse, type Motor, type MotorParseError } from '@openrocket-web/physics';

export const DEMO_MOTOR: Motor = (() => {
  const r = createMotor({
    designation: 'DEMO-C6',
    manufacturer: 'Demostración',
    diameter: 0.018,
    length: 0.07,
    delays: [3, 5, 7],
    plugged: false,
    propellantMass: 0.0108,
    totalMass: 0.0237,
    // Curva sintética inventada para esta aplicación; no corresponde a ningún motor comercial.
    points: [[0.05, 6], [0.15, 12], [0.3, 8], [1.5, 5.5], [1.8, 0]],
  });
  if (!r.ok) throw new Error(r.error);
  return r.motor;
})();

export interface LoadResult {
  readonly motors: readonly Motor[];
  readonly errors: readonly MotorParseError[];
}

/** Lee el texto de un archivo de motor según su extensión. */
export function parseMotorFile(name: string, text: string): LoadResult {
  const lower = name.toLowerCase();
  if (lower.endsWith('.rse')) return parseRse(text);
  if (lower.endsWith('.eng')) return parseEng(text);
  return { motors: [], errors: [{ message: `«${name}»: extensión no soportada (se esperaba .eng o .rse)` }] };
}

/** Añade motores a la biblioteca; los repetidos (mismo id) se sustituyen. */
export function mergeMotors(library: readonly Motor[], added: readonly Motor[]): Motor[] {
  const byId = new Map(library.map((m) => [m.id, m]));
  for (const m of added) byId.set(m.id, m);
  return [...byId.values()];
}

/**
 * Busca un motor por id. Si no hay coincidencia exacta, usa la designación cuando es única en la
 * biblioteca (los fabricantes se abrevian distinto según el archivo: «ES» frente a «Estes»).
 */
export function findMotor(library: readonly Motor[], motorId: string): Motor | undefined {
  const exact = library.find((m) => m.id === motorId);
  if (exact !== undefined) return exact;
  const designation = motorId.split('-').slice(1).join('-');
  if (designation === '') return undefined;
  const matches = library.filter((m) => m.id === designation || m.id.endsWith(`-${designation}`));
  return matches.length === 1 ? matches[0] : undefined;
}
